import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Eye, Users, Award, Shield, Clock, 
  CheckCircle, ArrowRight, Building2, Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f4acee42-6143-402b-b6a2-a84038bb5df0/artifacts/wp0qjcw1_WhatsApp%20Image%202026-03-11%20at%2011.51.52%20AM.jpeg";

const values = [
  {
    icon: Shield,
    title: 'Trust & Integrity',
    description: 'Building lasting relationships with government and enterprise clients through transparent practices.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Delivering world-class solutions that exceed expectations and drive measurable outcomes.'
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'Working closely with stakeholders to understand and address their unique challenges.'
  },
  {
    icon: Clock,
    title: 'Reliability',
    description: 'Ensuring on-time delivery and 24/7 support for mission-critical systems.'
  }
];

const milestones = [
  { year: '2015', event: 'Company Founded', description: 'NSTU India established with a vision to serve Haryana government IT needs' },
  { year: '2018', event: 'First Major Project', description: 'Delivered municipal e-Governance platform for Nagar Parishad' },
  { year: '2020', event: 'Haryana Expansion', description: 'Expanded operations to multiple districts in Haryana' },
  { year: '2023', event: 'Property Tax Expertise', description: 'Specialized in property tax bill distribution systems' },
  { year: '2025', event: '50+ Projects', description: 'Milestone of 50 government projects delivered in Haryana' },
  { year: '2026', event: 'Multi-District Operations', description: 'Operating in Kurukshetra, Pehowa, Ladwa, Shahabad and more' }
];

const certifications = [
  'ISO 27001 Certified',
  'STQC Empanelled',
  'GeM Registered',
  'Haryana Govt Vendor',
  'MSME Certified',
  'GST Registered'
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#0f1419]">
      {/* Hero Section */}
      <section className="relative py-20 bg-nstu-green-800" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-nstu-gold-400 text-[#0f1419] mb-6">About NSTU</Badge>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Building Haryana's Digital Future
              </h1>
              <p className="text-gray-300 text-lg mb-8">
                NSTU India Pvt. Ltd. (National Services Technical Unit) is a leading IT solutions 
                provider specializing in Haryana government and enterprise digital transformation projects.
              </p>
              <div className="flex gap-4">
                <Link to="/government-projects">
                  <Button className="bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="about-projects-btn">
                    <Landmark className="w-4 h-4 mr-2" />
                    Our Projects
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-nstu-green-800" data-testid="about-contact-btn">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src={LOGO_URL}
                alt="NSTU India Logo"
                className="w-64 h-64 object-contain rounded-full bg-white p-4 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#141a22]" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="bg-[#1a222d] border-l-4 border-nstu-gold-400 border-t-0 border-r-0 border-b-0">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-nstu-green-800 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">Our Mission</h2>
                <p className="text-gray-400 leading-relaxed">
                  To accelerate Haryana's digital transformation by providing innovative, secure, and 
                  scalable IT solutions to government departments and enterprises. We aim to bridge 
                  the gap between policy vision and technological implementation, ensuring citizen-centric 
                  digital services reach every corner of the state.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#1a222d] border-l-4 border-nstu-green-800 border-t-0 border-r-0 border-b-0">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-nstu-gold-400 flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-[#0f1419]" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">Our Vision</h2>
                <p className="text-gray-400 leading-relaxed">
                  To be the most trusted and preferred technology partner for government organizations 
                  and enterprises in Haryana. We envision a digitally empowered state where technology 
                  enables efficient governance, transparent administration, and improved quality of 
                  life for every citizen.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-[#0f1419]" data-testid="core-values">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-gold-400/20 text-nstu-gold-400 border border-nstu-gold-400/30 mb-4">Our Foundation</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Core Values That Drive Us
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our values form the bedrock of every project we undertake and every relationship we build
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <Card key={value.title} className="text-center card-hover bg-[#1a222d] border-gray-800">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-full bg-nstu-green-800/30 flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="w-8 h-8 text-nstu-gold-400" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-[#141a22]" data-testid="timeline">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-gold-400/20 text-nstu-gold-400 border border-nstu-gold-400/30 mb-4">Our Journey</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Years of Excellence in Haryana
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-700" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={milestone.year}
                  className={`flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <Card className="inline-block card-hover bg-[#1a222d] border-gray-800">
                      <CardContent className="p-6">
                        <span className="text-nstu-gold-400 font-bold text-lg">{milestone.year}</span>
                        <h3 className="font-heading text-xl font-bold text-white mt-2">
                          {milestone.event}
                        </h3>
                        <p className="text-gray-400 mt-2">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden lg:flex w-12 h-12 rounded-full bg-nstu-green-800 items-center justify-center z-10">
                    <div className="w-4 h-4 rounded-full bg-nstu-gold-400" />
                  </div>
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-nstu-green-800" data-testid="certifications">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-nstu-gold-400 text-[#0f1419] mb-4">Credentials</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Certifications & Empanelments
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Our credentials reflect our commitment to quality, security, and compliance
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {certifications.map((cert) => (
              <div 
                key={cert}
                className="bg-white/10 backdrop-blur rounded-lg p-4 text-center hover:bg-white/20 transition-colors"
              >
                <CheckCircle className="w-8 h-8 text-nstu-gold-400 mx-auto mb-3" />
                <p className="text-white text-sm font-medium">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-[#0f1419]" data-testid="team">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-nstu-gold-400/20 text-nstu-gold-400 border border-nstu-gold-400/30 mb-4">Our Team</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Experienced Professionals Dedicated to Haryana
              </h2>
              <p className="text-gray-400 mb-6">
                Our team comprises experienced IT professionals, domain experts, and project managers 
                who bring extensive experience in Haryana government IT projects. We understand the 
                unique challenges of property tax systems and municipal digitization.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  '50+ skilled IT professionals',
                  'Dedicated government project teams',
                  'Property tax system specialists',
                  '24/7 support and maintenance teams',
                  'Pan-Haryana delivery capability'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-nstu-gold-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button className="bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 btn-lift font-semibold" data-testid="join-team-btn">
                  Join Our Team
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="NSTU Team"
                className="rounded-2xl shadow-2xl border border-gray-700"
              />
              <div className="absolute -bottom-6 -right-6 bg-nstu-gold-400 text-[#0f1419] p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">50+</div>
                <div className="text-sm font-medium">Team Members</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#141a22]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Partner with Us?
          </h2>
          <p className="text-gray-400 mb-8">
            Let's discuss how NSTU can help transform your organization's digital capabilities
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 btn-lift font-semibold">
                Get In Touch
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-nstu-gold-400 text-nstu-gold-400 hover:bg-nstu-gold-400 hover:text-[#0f1419]">
                Explore Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
